import * as path from 'node:path';
import { createHash } from 'node:crypto';
import type { InstalledMarker } from './materialize.js';

/**
 * Abilities-head digest (spec §4, P1 shadow): one stable sha256 over sorted
 * `<slug>:<checksum>` lines. The SAME formula backs the server-side declared
 * head (computeActiveAbilitiesVersion) and the disk-grounded drive head, so
 * the two are directly comparable. Empty set = sha256('').
 */
export function digestAbilityLines(lines: string[]): string {
  return createHash('sha256').update([...lines].sort().join('\n')).digest('hex');
}

/** Line value used when a bundle dir has no parseable .installed marker. */
export const MISSING_MARKER_SENTINEL = 'missing-marker';

export interface AbilityDiskReader {
  readdir(p: string): Promise<string[]>;
  isDirectory(p: string): Promise<boolean>;
  readFile(p: string): Promise<string | null>;
}

/**
 * Disk-grounded drive head: walk `<root>/<slug>/.installed` markers and digest
 * `slug:checksum` lines. A missing/corrupt marker contributes the sentinel so
 * the head visibly diverges from the declared head (that IS the drift signal).
 * P1 limitation (accepted in the spec's phase plan): markers are trusted for
 * content — full byte re-verification lands with P2's repair loop.
 */
export async function computeDriveHeadFromDisk(
  root: string,
  reader: AbilityDiskReader,
): Promise<string> {
  let entries: string[] = [];
  try { entries = await reader.readdir(root); } catch { /* no drive yet */ }
  const lines: string[] = [];
  for (const name of entries) {
    if (name.startsWith('.')) continue;
    const dir = path.posix.join(root, name);
    try {
      if (!(await reader.isDirectory(dir))) continue;
      const raw = await reader.readFile(path.posix.join(dir, '.installed'));
      let checksum = MISSING_MARKER_SENTINEL;
      if (raw) {
        try {
          const marker = JSON.parse(raw) as InstalledMarker;
          if (typeof marker.checksum === 'string' && marker.checksum) checksum = marker.checksum;
        } catch { /* corrupt marker → sentinel */ }
      }
      lines.push(`${name}:${checksum}`);
    } catch { /* tolerate single-dir failure; omitted dir still flips the head */ }
  }
  return digestAbilityLines(lines);
}
